<template>
  <BaseModal
    :heading="$t('nav.settings')"
    grey
    parentRoute="integrations"
    small
    :loading="loading"
    useForm
    @submit="submit"
  >
    <BaseCard v-if="result">
      <Flex
        direction="column"
        :gap=".5"
      >
        <Flex>
          <BaseText bold>API key:</BaseText>
          <BaseText v-test="'fiskaly-sign-api-key'"
            >{{ result.fiskalyIntegration?.apiKey }}</BaseText
          >
        </Flex>
        <Flex>
          <BaseText bold>API secret:</BaseText>
          <BaseText
            v-test="'fiskaly-sign-sec-hidden'"
            v-if="!showSec"
            >*******************************</BaseText
          >
          <BaseText
            v-test="'fiskaly-sign-sec-visible'"
            v-else
            >{{ result.fiskalyIntegration?.apiSecret }}</BaseText
          >
          <BaseIcon
            v-if="!user?.impersonated"
            v-test="'fiskaly-sign-sec-toggle'"
            @click="toggleSec"
            :name="showSec ? 'eye' : 'eye-off'"
          />
        </Flex>
        <Flex>
          <BaseText bold>Device fingerprint:</BaseText>
          <BaseText v-test="'fiskaly-sign-device-fingerprint'"
            >{{ result.fiskalyIntegration?.system?.deviceFingerprint }}</BaseText
          >
        </Flex>
        <Flex>
          <BaseText bold>UUID:</BaseText>
          <BaseText v-test="'fiskaly-sign-uuid'"
            >{{ result.fiskalyIntegration?.system?.uuid }}</BaseText
          >
        </Flex>
        <Flex>
          <BaseText bold>State:</BaseText>
          <BaseText v-test="'fiskaly-sign-state'"
            >{{ result.fiskalyIntegration?.system?.state }}</BaseText
          >
        </Flex>
      </Flex>
    </BaseCard>
    <template
      v-if="result && result.fiskalyIntegration?.system?.state !== 'DECOMMISSIONED'"
      #footer
    >
      <BaseButton
        v-test="'decommission-fiskaly'"
        submitForm
        color="error"
        :loading="loadingDecommissionSystem"
        >Decommision</BaseButton
      >
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useRouter } from 'vue-router';
import { modal } from '@/helpers/ui';
import { useFiskalySystemStore } from '@/stores/fiskaly-system';
import { useLocationsStore } from '@/stores/locations';
import { useUserStore } from '@/stores/user';
import {
  DECOMMISSION_SYSTEM_MUTATION,
  FISKALY_SIGN_INFO_QUERY,
} from './graphql';

const router = useRouter();

const { result, loading } = useQuery(FISKALY_SIGN_INFO_QUERY);
const { user } = useUserStore();

const {
  mutate: decommissionSystem,
  onDone,
  loading: loadingDecommissionSystem,
} = useMutation(DECOMMISSION_SYSTEM_MUTATION);

const emit = defineEmits(['updated']);

// Snapshot of the location the decommission request was scoped to (via the
// X-Public-Location-Id header) — read again after the mutation settles it could
// point at a different location if the user switched mid-flight.
let decommissionedLocationPublicId: string | undefined;

onDone((result) => {
  if (
    result?.data?.decommissionSystem?.success &&
    decommissionedLocationPublicId
  ) {
    useFiskalySystemStore().clearSystemUuid(decommissionedLocationPublicId);
  }
  router.push({ name: 'integrations' });
  emit('updated');
});

const submit = () => {
  modal('confirmation', {
    type: 'cancel',
  }).then(() => {
    decommissionedLocationPublicId = useLocationsStore().location?.publicId;
    decommissionSystem({ input: {} });
  });
};

const showSec = ref(false);

const toggleSec = () => {
  showSec.value = !showSec.value;
};
</script>
